import { drawDOM, exportPDF } from "@progress/kendo-drawing";

// @progress/kendo-react-pdf's PDFExport.save() is unreliable: it can silently produce a
// blank PDF even when the DOM it exports is already fully rendered - confirmed by direct
// testing (reproduced live, in a real browser, against real invoice data) across many
// attempts, in inconsistent order: sometimes the 1st try is blank and a retry is fine,
// sometimes the reverse. There's no fixed "which attempt is bad" to simply skip, and
// save() gives no way to inspect what it produced before it's already been downloaded to
// the user.
//
// generateVerifiedPdfDataUri() drives the same underlying pipeline directly
// (drawDOM -> exportPDF, exactly what save() does internally before handing off to
// saveAs()) so the resulting PDF data can be checked BEFORE it ever reaches the user: if
// the decoded byte size looks like the known-bad blank-page case, it's regenerated - up
// to a few times. Callers should only ever pass the result to saveAs() themselves - the
// user never sees a bad file appear.
const BLANK_PDF_BYTE_THRESHOLD = 8000; // a genuine blank A4 page export is ~1KB; any real content is tens of KB+
const MAX_EXPORT_ATTEMPTS = 4;

const dataUriByteLength = (dataUri) => {
    const base64 = (dataUri || "").split(",")[1] || "";
    return Math.floor((base64.length * 3) / 4);
};

export const generateVerifiedPdfDataUri = async (rootEl, drawOptions) => {
    let lastDataUri = null;
    for (let attempt = 1; attempt <= MAX_EXPORT_ATTEMPTS; attempt++) {
        // eslint-disable-next-line no-await-in-loop
        const group = await drawDOM(rootEl, drawOptions);
        // eslint-disable-next-line no-await-in-loop
        const dataUri = await exportPDF(group, { multiPage: true, paperSize: drawOptions.paperSize });
        lastDataUri = dataUri;
        if (dataUriByteLength(dataUri) >= BLANK_PDF_BYTE_THRESHOLD) {
            return dataUri;
        }
    }
    // Every attempt looked blank - hand back the last one anyway rather than silently
    // producing nothing; this should be exceedingly rare given testing so far.
    return lastDataUri;
};

// Courtesy wait, not the fix for the blank-PDF issue above: waits for every <img> inside
// a container to finish loading (or error out) before an export reads the DOM.
export const waitForImagesIn = (containerId) => {
    const container = document.getElementById(containerId);
    if (!container) return Promise.resolve();
    const imgs = Array.from(container.querySelectorAll("img"));
    return Promise.all(
        imgs.map((img) => {
            if (img.complete) return Promise.resolve();
            return new Promise((resolve) => {
                img.addEventListener("load", resolve, { once: true });
                img.addEventListener("error", resolve, { once: true });
            });
        })
    );
};

// Waits one full paint cycle (double rAF) plus web font loading.
export const waitForStablePaint = () =>
    new Promise((resolve) => {
        requestAnimationFrame(() => requestAnimationFrame(resolve));
    }).then(() => (document.fonts && document.fonts.ready ? document.fonts.ready : null));
